import { useEffect, useMemo, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { listCustomers } from '../api/customers';
import { listOrders } from '../api/orders';
import { listBatches, updateBatch, removeBatch } from '../api/batches';
import { createTask, createOrderTask } from '../api/tasks';
import { listGroups } from '../api/groups';
import { fmtDate, fmtVND } from '../utils/format';
import { RatioBadge } from '../components/Chips';

// sem: màu ngữ nghĩa của chip lọc theo thiết kế mới (đỏ = rất thấp, hổ phách = thấp).
const SCAN_FILTERS = [
  { id: 'verylow', label: 'Cọc rất thấp (<20%)', sem: 'sem-danger' },
  { id: 'low', label: 'Cọc thấp (<60%)', sem: 'sem-warn' },
  { id: 'zero', label: 'Chưa cọc (0%)' },
  { id: 'full', label: 'Đã đủ cọc (≥100%)' },
  { id: 'debt', label: 'Còn công nợ' },
];

// Bộ lọc cho bảng so sánh tuần trước / tuần này (giống extension).
const CMP_FILTERS = [
  { id: 'revUp', label: '💰 ▲ Doanh thu tăng', test: (r) => r.curr > r.prev },
  { id: 'revDown', label: '💰 ▼ Doanh thu giảm', test: (r) => r.curr < r.prev },
  { id: 'ordUp', label: '📦 ▲ Số đơn tăng', test: (r) => r.currCount > r.prevCount },
  { id: 'ordDown', label: '📦 ▼ Số đơn giảm', test: (r) => r.currCount < r.prevCount },
];

// Loại đợt quét suy ra từ nội dung: có đơn -> đợt đơn hàng; còn lại -> đợt khách hàng.
function batchKind(b) {
  return b.orderCount > 0 && (b.customerCount || 0) === 0 ? 'orders' : 'customers';
}

// Gom đơn theo khách hàng, tách doanh thu 2 kỳ tuần trước / tuần này theo mốc periodMid.
function buildComparison(orders, periodMid) {
  const mid = new Date(periodMid).getTime();
  const map = {};
  for (const o of orders) {
    const key = (o.customerNo || '') + '||' + (o.customerName || '(không tên)');
    if (!map[key]) {
      map[key] = {
        sale: o.sale || '—',
        saleCode: o.saleCode || '',
        code: o.customerNo || '',
        name: o.customerName || '(không tên)',
        prev: 0,
        curr: 0,
        prevCount: 0,
        currCount: 0,
      };
    }
    const rec = map[key];
    const t = Number(o.value) || 0;
    const od = o.statusDate ? new Date(o.statusDate).getTime() : null;
    if (od != null && od < mid) {
      rec.prev += t;
      rec.prevCount++;
    } else {
      rec.curr += t;
      rec.currCount++;
    }
  }
  return Object.values(map).sort((a, b) => b.curr - a.curr);
}

export default function ScanPage({ onCreated }) {
  const { me } = useAuth();
  // GĐ và Trưởng phòng (nhóm KD) đều được tạo công việc. TP bị backend giới hạn về nhóm của mình.
  const canCreate = me.role === 'GD' || me.role === 'TP';
  const toast = useToast();
  const { openModal, closeModal } = useModal();

  const [batches, setBatches] = useState([]);
  const [list, setList] = useState([]); // khách hàng
  const [orderList, setOrderList] = useState([]); // đơn hàng
  const [batchId, setBatchId] = useState('all');
  const [saleFilter, setSaleFilter] = useState('all');
  const [filters, setFilters] = useState(new Set());
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [batchStatusFilter, setBatchStatusFilter] = useState('active'); // active | saved | all
  const [batchBusy, setBatchBusy] = useState(false);
  const [orderTab, setOrderTab] = useState('compare'); // compare | list (chỉ với đợt đơn hàng)
  const [cmpFilters, setCmpFilters] = useState(new Set()); // lọc bảng so sánh
  const [cmpPicked, setCmpPicked] = useState(new Set()); // KH đã tích ở bảng so sánh (key=code||name)

  // Đợt đang chọn + xác định đang xem đơn hàng hay khách hàng.
  const selectedBatch = batchId === 'all' ? null : batches.find((b) => String(b._id) === String(batchId));
  const isOrderView = !!(selectedBatch && batchKind(selectedBatch) === 'orders');

  function reloadBatches() {
    return listBatches().then(setBatches);
  }

  function reloadCustomers() {
    setLoading(true);
    return listCustomers({
      batch: batchId,
      sale: saleFilter,
      filters: [...filters].join(','),
      sortCol: sortCol || undefined,
      sortDir,
    })
      .then(setList)
      .finally(() => setLoading(false));
  }

  function reloadOrders() {
    setLoading(true);
    return listOrders({ batch: batchId, sale: saleFilter })
      .then(setOrderList)
      .finally(() => setLoading(false));
  }

  function reloadCurrent() {
    return isOrderView ? reloadOrders() : reloadCustomers();
  }

  useEffect(() => {
    reloadBatches();
  }, []);

  useEffect(() => {
    if (isOrderView) reloadOrders();
    else reloadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, saleFilter, filters, sortCol, sortDir]);

  const sales = useMemo(() => {
    const src = isOrderView ? orderList : list;
    return [...new Set(src.map((x) => x.sale).filter(Boolean))];
  }, [isOrderView, orderList, list]);

  const hasPeriod = !!selectedBatch?.periodMid;
  const comparison = useMemo(
    () => (isOrderView && hasPeriod ? buildComparison(orderList, selectedBatch.periodMid) : []),
    [isOrderView, hasPeriod, orderList, selectedBatch]
  );
  const comparisonShown = useMemo(() => {
    const active = CMP_FILTERS.filter((f) => cmpFilters.has(f.id));
    return active.length ? comparison.filter((r) => active.every((f) => f.test(r))) : comparison;
  }, [comparison, cmpFilters]);

  function toggleCmpFilter(id) {
    const next = new Set(cmpFilters);
    next.has(id) ? next.delete(id) : next.add(id);
    setCmpFilters(next);
  }

  const cmpKey = (r) => r.code + '||' + r.name;
  function toggleCmpPick(key, on) {
    const next = new Set(cmpPicked);
    on ? next.add(key) : next.delete(key);
    setCmpPicked(next);
  }
  function toggleCmpAll(on) {
    const next = new Set(cmpPicked);
    comparisonShown.forEach((r) => (on ? next.add(cmpKey(r)) : next.delete(cmpKey(r))));
    setCmpPicked(next);
  }

  async function openCreateOrderTask() {
    const rows = comparison.filter((r) => cmpPicked.has(cmpKey(r)));
    if (!rows.length) return toast('Hãy tích chọn KH ở bảng so sánh', 'warn');
    openModal(
      <OrderTaskForm
        rows={rows}
        onCancel={closeModal}
        onSubmit={async (form) => {
          try {
            const res = await createOrderTask({
              ...form,
              period: selectedBatch
                ? { from: selectedBatch.periodFrom, mid: selectedBatch.periodMid, to: selectedBatch.periodTo }
                : null,
              customers: rows.map((r) => ({
                customerNo: r.code,
                customerName: r.name,
                sale: r.sale,
                saleCode: r.saleCode,
                prevRevenue: r.prev,
                currRevenue: r.curr,
                prevCount: r.prevCount,
                currCount: r.currCount,
              })),
            });
            setCmpPicked(new Set());
            closeModal();
            let msg = `Đã tạo ${res.tasksCreated} đầu việc, chờ trưởng phòng xác nhận`;
            if (res.skipped && res.skipped.length) msg += ` · Bỏ qua ${res.skipped.length} KH không khớp nhân viên`;
            toast(msg);
            onCreated && onCreated();
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  function toggleFilter(id) {
    const next = new Set(filters);
    next.has(id) ? next.delete(id) : next.add(id);
    setFilters(next);
  }

  function togglePick(id, on) {
    const next = new Set(picked);
    on ? next.add(id) : next.delete(id);
    setPicked(next);
  }

  function toggleAll(on) {
    const next = new Set(picked);
    list.forEach((c) => (on ? next.add(c._id) : next.delete(c._id)));
    setPicked(next);
  }

  function sortBy(col) {
    if (sortCol === col) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  }
  function arrow(col) {
    if (sortCol !== col) return <span style={{ color: '#64748b' }}>↕</span>;
    return sortDir === 'asc' ? <span style={{ color: '#4ade80' }}>▲</span> : <span style={{ color: '#f87171' }}>▼</span>;
  }

  async function openCreateTask() {
    if (!canCreate) return toast('Bạn không có quyền tạo công việc', 'warn');
    const pickedList = list.filter((c) => picked.has(c._id));
    if (!pickedList.length) return toast('Hãy tích chọn KH ở danh sách quét trước', 'warn');
    const groups = await listGroups();
    openModal(
      <CreateTaskForm
        picked={pickedList}
        groups={groups}
        canPickGroup={me.role === 'GD'}
        onCancel={closeModal}
        onSubmit={async (form) => {
          try {
            const res = await createTask({ ...form, custIds: pickedList.map((c) => c._id) });
            setPicked(new Set());
            closeModal();
            toast(res?.message || 'Đã tạo & đẩy công việc xuống nhóm');
            onCreated && onCreated();
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  async function saveBatch(b) {
    if (batchBusy) return;
    setBatchBusy(true);
    try {
      const next = b.status === 'saved' ? 'active' : 'saved';
      await updateBatch(b._id, { status: next });
      toast(next === 'saved' ? 'Đã lưu đợt để dùng sau' : 'Đã bỏ lưu đợt');
      await reloadBatches();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBatchBusy(false);
    }
  }

  async function deleteBatch(b) {
    if (batchBusy) return;
    const isOrders = batchKind(b) === 'orders';
    const ok = await confirm(
      isOrders
        ? `Xóa đợt đơn hàng "${b.name}"? Toàn bộ đơn trong đợt sẽ bị xóa.`
        : `Xóa đợt "${b.name}"?\nKH chưa gắn vào công việc sẽ bị xóa. KH đang dùng trong công việc sẽ được giữ lại.`
    );
    if (!ok) return;
    setBatchBusy(true);
    try {
      const res = await removeBatch(b._id);
      let msg = 'Đã xóa đợt';
      if (res.deletedOrders) msg += ` · ${res.deletedOrders} đơn`;
      if (res.deletedCustomers) msg += ` · ${res.deletedCustomers} KH`;
      if (res.keptCustomers) msg += ` · giữ ${res.keptCustomers} KH (đang dùng)`;
      toast(msg);
      if (String(batchId) === String(b._id)) setBatchId('all');
      await reloadBatches();
      await reloadCurrent();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBatchBusy(false);
    }
  }

  // Xóa TẤT CẢ đợt khách hàng đang lưu (báo cáo tỷ lệ cọc đẩy về từ extension).
  async function deleteAllCustomerBatches() {
    if (batchBusy) return;
    const targets = scanBatches.filter((b) => b.customerCount > 0);
    if (!targets.length) return toast('Không có đợt khách hàng nào để xóa', 'warn');
    if (
      !await confirm(
        `Xóa TOÀN BỘ ${targets.length} đợt khách hàng đã lưu?\nKH chưa gắn vào công việc sẽ bị xóa, KH đang dùng trong công việc được giữ lại.`
      )
    )
      return;
    setBatchBusy(true);
    try {
      let delCust = 0;
      for (const b of targets) {
        const res = await removeBatch(b._id);
        delCust += res.deletedCustomers || 0;
      }
      toast(`Đã xóa ${targets.length} đợt · ${delCust} KH`);
      setBatchId('all');
      await reloadBatches();
      await reloadCurrent();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBatchBusy(false);
    }
  }

  // Đợt có dữ liệu (đơn hoặc KH), lọc theo trạng thái. Bỏ đợt "Danh sách KH (toàn bộ)"
  // vì danh sách KH master chỉ thuộc màn Quản lý khách hàng, không dùng để giao việc.
  const scanBatches = batches
    .filter((b) => b.kind !== 'customers-master')
    .filter((b) => b.customerCount > 0 || b.orderCount > 0)
    .filter((b) => (batchStatusFilter === 'all' ? true : (b.status || 'active') === batchStatusFilter));

  const sub =
    me.role === 'GD'
      ? 'Toàn bộ danh sách đẩy về từ extension'
      : me.role === 'TP'
        ? 'Danh sách của bạn và nhân viên trong nhóm'
        : 'Danh sách của bạn';

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Danh sách quét từ extension</h2>
          <div className="sub">{sub}</div>
        </div>
        {canCreate && !isOrderView && (
          <button className="btn" onClick={openCreateTask} disabled={!picked.size}>
            + Tạo công việc ({picked.size} KH)
          </button>
        )}
      </div>

      <details className="helpbox">
        <summary>Luồng hoạt động</summary>
        <p>
          Mỗi lần extension đẩy dữ liệu xuống là một "đợt" riêng (đơn hàng hoặc khách hàng). Chọn đợt để xem. Với đợt
          khách hàng, Giám đốc có thể lọc, tích chọn KH rồi bấm "Tạo công việc". Sau khi xử lý xong, có thể <b>Lưu</b>{' '}
          đợt để dùng lại, hoặc <b>Xóa</b> nếu không cần.
        </p>
      </details>

      {/* ===== Quản lý các đợt ===== */}
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>📦 Các đợt đẩy về ({scanBatches.length})</h3>
          <div className="row" style={{ gap: 6 }}>
            {['active', 'saved', 'all'].map((s) => (
              <button
                key={s}
                className={`filterBtn ${batchStatusFilter === s ? 'on' : ''}`}
                onClick={() => setBatchStatusFilter(s)}
              >
                {s === 'active' ? 'Đang xử lý' : s === 'saved' ? 'Đã lưu' : 'Tất cả'}
              </button>
            ))}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Tên đợt</th>
              <th className="c">Loại</th>
              <th>Ngày</th>
              <th>Người quét</th>
              <th className="c">Số lượng</th>
              <th className="c">Trạng thái</th>
              <th className="c">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {scanBatches.length ? (
              scanBatches.map((b) => {
                const selected = String(batchId) === String(b._id);
                const saved = (b.status || 'active') === 'saved';
                const isOrders = batchKind(b) === 'orders';
                return (
                  <tr key={b._id} style={selected ? { background: 'rgba(91,140,255,.10)' } : {}}>
                    <td>
                      <b>{b.name}</b>
                      {selected && <span className="chip doing" style={{ marginLeft: 8 }}>Đang xem</span>}
                    </td>
                    <td className="c">
                      {isOrders ? (
                        <span className="chip doing">Đơn hàng</span>
                      ) : (
                        <span className="chip todo">Khách hàng</span>
                      )}
                    </td>
                    <td className="small muted">{fmtDate(b.date)}</td>
                    {/* Đợt cũ / quét theo lịch không có scannedByName -> "Tự động" */}
                    <td className="small">{b.scannedByName || <span className="muted">Tự động</span>}</td>
                    <td className="c">{isOrders ? `${b.orderCount} đơn` : `${b.customerCount} KH`}</td>
                    <td className="c">
                      {saved ? <span className="chip done">Đã lưu</span> : <span className="chip todo">Đang xử lý</span>}
                    </td>
                    <td className="c">
                      <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
                        <button className="btn sm ghost" onClick={() => setBatchId(b._id)} disabled={selected}>
                          Xem
                        </button>
                        {me.role === 'GD' && (
                          <>
                            <button className="btn sm ok" onClick={() => saveBatch(b)} disabled={batchBusy}>
                              {saved ? 'Bỏ lưu' : 'Lưu'}
                            </button>
                            <button className="btn sm danger" onClick={() => deleteBatch(b)} disabled={batchBusy}>
                              Xóa
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} className="empty">
                  Chưa có đợt nào. Hãy đẩy dữ liệu từ extension.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ===== Bảng dữ liệu của đợt đang xem ===== */}
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          <div className="small">
            Đang xem:{' '}
            <b>{batchId === 'all' ? 'Tất cả đợt khách hàng' : selectedBatch?.name || '—'}</b>
            <span className="muted"> · {isOrderView ? `${orderList.length} đơn` : `${list.length} KH`}</span>
          </div>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            {batchId !== 'all' && (
              <button className="btn ghost sm" onClick={() => setBatchId('all')}>
                Xem tất cả KH
              </button>
            )}
            {me.role === 'GD' && !isOrderView && batchId === 'all' && (
              <button className="btn danger sm" onClick={deleteAllCustomerBatches} disabled={batchBusy}>
                🗑 Xóa toàn bộ
              </button>
            )}
            <select style={{ width: 'auto', minWidth: 160 }} value={saleFilter} onChange={(e) => setSaleFilter(e.target.value)}>
              <option value="all">Tất cả nhân viên</option>
              {sales.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isOrderView ? (
          /* ----- ĐƠN HÀNG: so sánh tuần trước / tuần này + danh sách đơn ----- */
          <>
            {hasPeriod && (
              <>
                <div className="row" style={{ gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                  <button
                    className={`filterBtn ${orderTab === 'compare' ? 'on' : ''}`}
                    onClick={() => setOrderTab('compare')}
                  >
                    So sánh tuần trước / tuần này
                  </button>
                  <button className={`filterBtn ${orderTab === 'list' ? 'on' : ''}`} onClick={() => setOrderTab('list')}>
                    Danh sách đơn
                  </button>
                </div>
                <div className="small muted" style={{ marginBottom: 10 }}>
                  Tuần trước: <b>{fmtDate(selectedBatch.periodFrom)}</b> –{' '}
                  <b>{fmtDate(new Date(new Date(selectedBatch.periodMid).getTime() - 864e5))}</b> · Tuần này:{' '}
                  <b>{fmtDate(selectedBatch.periodMid)}</b> – <b>{fmtDate(selectedBatch.periodTo)}</b>
                </div>
              </>
            )}

            {hasPeriod && orderTab === 'compare' ? (
              <>
                <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  {CMP_FILTERS.map((f) => {
                    const n = comparison.filter((r) => f.test(r)).length;
                    const on = cmpFilters.has(f.id);
                    return (
                      <button key={f.id} className={`filterBtn ${on ? 'on' : ''}`} onClick={() => toggleCmpFilter(f.id)}>
                        {on ? '✓ ' : ''}
                        {f.label} ({n})
                      </button>
                    );
                  })}
                  {cmpFilters.size > 0 && (
                    <button className="filterBtn clear" onClick={() => setCmpFilters(new Set())}>
                      ✕ Xóa lọc
                    </button>
                  )}
                  <div className="spacer"></div>
                  {canCreate && cmpPicked.size > 0 && (
                    <button className="btn ghost sm" onClick={() => setCmpPicked(new Set())}>
                      Bỏ chọn ({cmpPicked.size})
                    </button>
                  )}
                  {canCreate && (
                    <button className="btn sm" onClick={openCreateOrderTask} disabled={!cmpPicked.size}>
                      + Tạo công việc ({cmpPicked.size} KH)
                    </button>
                  )}
                  <span className="small muted">{comparisonShown.length} KH</span>
                </div>
                <table>
                  <thead>
                    <tr>
                      {canCreate && (
                        <th className="c">
                          <input type="checkbox" onChange={(e) => toggleCmpAll(e.target.checked)} />
                        </th>
                      )}
                      <th>Sale</th>
                      <th>Khách hàng</th>
                      <th className="c">Tuần trước (đơn)</th>
                      <th className="r">DT tuần trước</th>
                      <th className="c">Tuần này (đơn)</th>
                      <th className="r">DT tuần này</th>
                      <th className="c">Chênh lệch đơn</th>
                      <th className="r">Chênh lệch DT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="empty">
                          Đang tải...
                        </td>
                      </tr>
                    ) : comparisonShown.length ? (
                      comparisonShown.map((r) => {
                      const diff = r.curr - r.prev;
                      const color = diff > 0 ? 'var(--ok)' : diff < 0 ? 'var(--danger)' : 'var(--muted)';
                      const cntDiff = (r.currCount || 0) - (r.prevCount || 0);
                      const cntColor = cntDiff > 0 ? 'var(--ok)' : cntDiff < 0 ? 'var(--danger)' : 'var(--muted)';
                      const key = r.code + '||' + r.name;
                      return (
                        <tr key={key}>
                          {canCreate && (
                            <td className="c">
                              <input
                                type="checkbox"
                                checked={cmpPicked.has(key)}
                                onChange={(e) => toggleCmpPick(key, e.target.checked)}
                              />
                            </td>
                          )}
                          <td>
                            {r.sale}
                            {r.saleCode ? <div className="small muted">{r.saleCode}</div> : null}
                          </td>
                          <td>
                            {r.name}
                            {r.code ? <div className="small muted">{r.code}</div> : null}
                          </td>
                          <td className="c small">{r.prevCount}</td>
                          <td className="r">{fmtVND(r.prev)}</td>
                          <td className="c small">{r.currCount}</td>
                          <td className="r">{fmtVND(r.curr)}</td>
                          <td className="c" style={{ color: cntColor, fontWeight: 700 }}>
                            {cntDiff > 0 ? '▲ ' : cntDiff < 0 ? '▼ ' : ''}
                            {cntDiff === 0 ? '0' : Math.abs(cntDiff)} đơn
                          </td>
                          <td className="r" style={{ color, fontWeight: 700 }}>
                            {diff > 0 ? '▲ ' : diff < 0 ? '▼ ' : ''}
                            {fmtVND(Math.abs(diff))}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} className="empty">
                        Không có dữ liệu so sánh.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Mã đơn</th>
                    <th>Khách hàng</th>
                    <th>Sale</th>
                    <th>Nhóm</th>
                    <th>Trạng thái</th>
                    <th className="c">Tuyến</th>
                    <th className="c">Ngày TT</th>
                    <th className="c">Dự kiến giao</th>
                    <th className="r">Giá trị</th>
                    <th>TT thanh toán</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="empty">
                        Đang tải...
                      </td>
                    </tr>
                  ) : orderList.length ? (
                    orderList.map((o) => (
                      <tr key={o._id}>
                        <td>{o.code}</td>
                        <td>
                          {o.customerName || <span className="small muted">—</span>}
                          {o.customerNo ? <div className="small muted">{o.customerNo}</div> : null}
                        </td>
                        <td>
                          {o.sale || <span className="small muted">—</span>}
                          {o.saleCode ? <div className="small muted">{o.saleCode}</div> : null}
                        </td>
                        <td>{o.ownerGroup || <span className="small muted">—</span>}</td>
                        <td className="small">{o.statusText || '—'}</td>
                        <td className="c">{o.route || '—'}</td>
                        <td className="c small">{o.statusDate ? fmtDate(o.statusDate) : '—'}</td>
                        <td className="c small">{o.eta ? fmtDate(o.eta) : <span className="muted">—</span>}</td>
                        <td className="r">{fmtVND(o.value)}</td>
                        <td className="small">{o.paymentStatus || <span className="muted">—</span>}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="empty">
                        Không có đơn hàng nào trong phạm vi này.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </>
        ) : (
          /* ----- Bảng KHÁCH HÀNG (lọc + chọn tạo việc) ----- */
          <>
            <div className="row" style={{ gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="small muted">Lọc nhanh:</span>
              {SCAN_FILTERS.map((f) => (
                <button key={f.id} className={`filterBtn ${f.sem || ''} ${filters.has(f.id) ? 'on' : ''}`} onClick={() => toggleFilter(f.id)}>
                  {filters.has(f.id) ? '✓ ' : ''}
                  {f.label}
                </button>
              ))}
              {filters.size > 0 && (
                <button className="filterBtn clear" onClick={() => setFilters(new Set())}>
                  ✕ Xóa lọc
                </button>
              )}
              <div className="spacer"></div>
              {canCreate && picked.size > 0 && (
                <button className="btn ghost sm" onClick={() => setPicked(new Set())}>
                  Bỏ chọn ({picked.size})
                </button>
              )}
            </div>

            <table>
              <thead>
                <tr>
                  {canCreate && (
                    <th className="c">
                      <input type="checkbox" onChange={(e) => toggleAll(e.target.checked)} />
                    </th>
                  )}
                  <th>Mã KH</th>
                  <th>Tên KH</th>
                  <th>Nhân viên (sale)</th>
                  <th>Nhóm KD</th>
                  <th className="r" onClick={() => sortBy('total')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Tổng tiền {arrow('total')}
                  </th>
                  <th className="r" onClick={() => sortBy('paid')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Đã T.Toán {arrow('paid')}
                  </th>
                  <th className="r" onClick={() => sortBy('ratio')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Tỷ lệ cọc {arrow('ratio')}
                  </th>
                  <th className="r" onClick={() => sortBy('debt')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Công nợ {arrow('debt')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="empty">
                      Đang tải...
                    </td>
                  </tr>
                ) : list.length ? (
                  list.map((c) => (
                    <tr key={c._id}>
                      {canCreate && (
                        <td className="c">
                          <input type="checkbox" checked={picked.has(c._id)} onChange={(e) => togglePick(c._id, e.target.checked)} />
                        </td>
                      )}
                      <td>{c.code}</td>
                      <td>{c.name}</td>
                      <td>{c.sale}</td>
                      <td>{c.ownerGroup || <span className="small muted">—</span>}</td>
                      <td className="r">{fmtVND(c.total)}</td>
                      <td className="r">{fmtVND(c.paid)}</td>
                      <td className="r">
                        <RatioBadge r={c.ratio} />
                      </td>
                      <td className="r">{fmtVND(c.debt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="empty">
                      Không có KH phù hợp bộ lọc
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </>
  );
}

// Form tạo đầu việc từ bảng so sánh đơn hàng (không chọn nhóm — tự theo sale của KH).
function OrderTaskForm({ rows, onCancel, onSubmit }) {
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const dlDefault = new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10);
  const [deadline, setDeadline] = useState(dlDefault);
  const [busy, setBusy] = useState(false);

  // gom số KH theo sale để GĐ biết việc sẽ chia về những ai
  const bySale = {};
  rows.forEach((r) => {
    const k = r.sale || '(không rõ)';
    bySale[k] = (bySale[k] || 0) + 1;
  });

  async function submit() {
    if (busy) return;
    if (!title.trim()) return;
    setBusy(true);
    try {
      await onSubmit({ title: title.trim(), detail: detail.trim(), deadline });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Tạo đầu công việc</h3>
      <div className="field">
        <label>Tiêu đề công việc</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="vd: Chăm sóc KH doanh thu giảm tuần này" />
      </div>
      <div className="field">
        <label>Hướng dẫn / yêu cầu cụ thể</label>
        <textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Mô tả yêu cầu, cách xử lý KH..." />
      </div>
      <div className="field">
        <label>Hạn nhân viên hoàn thành</label>
        <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>
      <div className="demo-hint">
        Việc sẽ chia tự động theo nhân viên phụ trách KH, đẩy xuống <b>trưởng phòng</b> để xác nhận. Sau khi xác nhận,
        nhân viên mới thấy việc kèm các KH mình quản lý.
      </div>
      <div className="field">
        <label>Khách hàng đã chọn ({rows.length}) — phân theo nhân viên</label>
        <div>
          {Object.entries(bySale).map(([s, n]) => (
            <span className="tag" key={s}>
              {s}: {n} KH
            </span>
          ))}
        </div>
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={submit} disabled={busy || !title.trim()}>
          {busy ? 'Đang tạo…' : 'Tạo & đẩy xuống trưởng phòng'}
        </button>
      </div>
    </>
  );
}

function CreateTaskForm({ picked, groups, canPickGroup = true, onCancel, onSubmit }) {
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [group, setGroup] = useState('all'); // mặc định giao cho tất cả các nhóm
  const dlDefault = new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10);
  const [deadline, setDeadline] = useState(dlDefault);

  // Trạng thái kết quả (tuỳ chọn)
  const [useStatuses, setUseStatuses] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [statusInput, setStatusInput] = useState('');

  function addStatus() {
    const v = statusInput.trim();
    if (!v) return;
    if (statuses.includes(v)) {
      setStatusInput('');
      return;
    }
    setStatuses([...statuses, v]);
    setStatusInput('');
  }
  function removeStatus(s) {
    setStatuses(statuses.filter((x) => x !== s));
  }

  function submit() {
    const cleanStatuses = useStatuses ? statuses : [];
    onSubmit({ title, detail, group, deadline, statuses: cleanStatuses });
  }

  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Tạo công việc mới</h3>
      <div className="field">
        <label>Tiêu đề công việc</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="vd: Thu hồi công nợ nhóm KH cọc thấp" />
      </div>
      <div className="field">
        <label>Yêu cầu chi tiết</label>
        <textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Mô tả công việc cần làm..." />
      </div>
      <div className="two-col">
        {canPickGroup ? (
          <div className="field">
            <label>Giao cho nhóm kinh doanh</label>
            <select value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="all">★ Tất cả các nhóm (tự chia & giao theo từng nhân viên)</option>
              {groups.map((g) => (
                <option key={g._id} value={g._id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="field">
            <label>Giao cho</label>
            <div className="small muted" style={{ padding: '8px 0' }}>Nhóm của bạn (tự động)</div>
          </div>
        )}
        <div className="field">
          <label>Hạn nhân viên hoàn thành</label>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
        </div>
      </div>
      <div className="demo-hint">
        Hệ thống tự chia khách hàng theo từng nhân viên (theo sale của KH) và giao thẳng cho nhân viên đó. Trưởng phòng
        có thêm 1 ngày sau hạn này để kiểm tra & duyệt kết quả.
      </div>

      {/* Trạng thái kết quả tuỳ chọn */}
      <div className="field">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={useStatuses} onChange={(e) => setUseStatuses(e.target.checked)} />
          Tạo trạng thái kết quả để nhân viên chọn (vd: Đã liên hệ khách, KH đã quay lại…)
        </label>
      </div>
      {useStatuses && (
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <div className="row" style={{ gap: 8 }}>
            <input
              style={{ flex: 1 }}
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStatus())}
              placeholder="Nhập 1 trạng thái rồi bấm Thêm…"
            />
            <button className="btn sm" onClick={addStatus}>
              + Thêm
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            {statuses.length ? (
              statuses.map((s) => (
                <span className="tag" key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {s}
                  <span style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 700 }} onClick={() => removeStatus(s)}>
                    ×
                  </span>
                </span>
              ))
            ) : (
              <span className="small muted">Chưa có trạng thái nào. Thêm ít nhất 1 trạng thái.</span>
            )}
          </div>
          <div className="small muted" style={{ marginTop: 8 }}>
            Khi báo cáo, nhân viên chọn 1 trạng thái cho mỗi KH (vẫn note nội dung cụ thể). Sau khi TP duyệt, hệ thống
            tính tỷ lệ KH theo từng trạng thái (trên các KH được duyệt đạt).
          </div>
        </div>
      )}

      <div className="field">
        <label>Khách hàng đã chọn ({picked.length})</label>
        <div>
          {picked.map((c) => (
            <span className="tag" key={c._id}>
              {c.code} · {c.name} ({c.ratio}%)
            </span>
          ))}
        </div>
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={submit}>
          Tạo & đẩy xuống nhóm
        </button>
      </div>
    </>
  );
}
